// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Constants } from '../../Constants';
import { NOTIFICATION_TEMPLATES } from '../../components/AppContainer/ChatView/Notifications/NotificationConstants';
import { getScopeIcon } from '../../components/utils/IconUtils';
import { getErrorDetails } from '../../components/utils/TextUtils';
import { getParamValue } from '../../components/utils/UrlUtils';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { FeatureKeys } from '../../redux/features/app/AppState';
import { addAlert, setNotifications, toggleFeatureState, updateTokenUsage } from '../../redux/features/app/appSlice';
import { ChatState } from '../../redux/features/conversations/ChatState';
import { Conversations } from '../../redux/features/conversations/ConversationsState';
import {
    addConversation,
    addMessageToConversationFromUser,
    addMessagesToConversation,
    deleteConversation,
    editConversationIsSaved,
    setChatMemorySources,
    setConversationParticipants,
    setConversationUserFeedback,
    setConversations,
    setSelectedConversation,
    updateBotResponseStatus,
} from '../../redux/features/conversations/conversationsSlice';
import { Plugin } from '../../redux/features/plugins/PluginsState';
import { AuthHelper } from '../auth/AuthHelper';
import { AlertType } from '../models/AlertType';
import { ChatArchive } from '../models/ChatArchive';
import { AuthorRoles, ChatMessageType, IChatMessage, UserFeedback, UserFeedbackType } from '../models/ChatMessage';
import { IChatSession, ICreateChatSessionResponse } from '../models/ChatSession';
import { IChatUser } from '../models/ChatUser';
import { PlanState } from '../models/Plan';
import { ISiteInfo } from '../models/SelectedSites';
import { TokenUsage } from '../models/TokenUsage';
import { IUserPermissions } from '../models/UserPermissions';
import { IAskVariables } from '../semantic-kernel/model/Ask';
import { ContextVariable } from '../semantic-kernel/model/AskResult';
import { ChatArchiveService } from '../services/ChatArchiveService';
import { ChatService } from '../services/ChatService';
import { DocumentImportService } from '../services/DocumentImportService';

export interface GetResponseOptions {
    messageType: ChatMessageType;
    value: string;
    chatId: string;
    kernelArguments?: IAskVariables[];
    processPlan?: boolean;
    scope?: string;
}

export const useChat = () => {
    const dispatch = useAppDispatch();
    const { instance, inProgress } = useMsal();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);

    const botService = new ChatArchiveService();
    const chatService = new ChatService();
    const documentImportService = new DocumentImportService();

    //const botProfilePictures: string[] = [botIcon1, botIcon2, botIcon3, botIcon4, botIcon5];

    const userId = activeUserInfo?.id ?? '';
    const fullName = activeUserInfo?.username ?? '';
    const emailAddress = activeUserInfo?.email ?? '';
    const loggedInUser: IChatUser = {
        id: userId,
        fullName,
        emailAddress,
        photo: undefined, // TODO: [Issue #45] Make call to Graph /me endpoint to load photo
        online: true,
        isTyping: false,
    };

    const { plugins } = useAppSelector((state: RootState) => state.plugins);

    const getChatUserById = (id: string, chatId: string, users: IChatUser[]) => {
        if (id === `${chatId}-bot` || id.toLocaleLowerCase() === 'bot') return Constants.bot.profile;
        return users.find((user) => user.id === id);
    };

    const removeUnsavedChats = () => {
        Object.keys(conversations)
            .filter((chatId) => !conversations[chatId].isSaved)
            .forEach((chatId) => {
                dispatch(deleteConversation(chatId));
            });
    };

    const createChat = async (skipNotification?: boolean) => {
        removeUnsavedChats();

        const chatTitle = `${Constants.bot.profile.fullName} @ ${new Date().toLocaleString()}`;

        let chatScope = 'None';
        const lastSelectedChatScope = localStorage.getItem('lastSelectedChatScope');
        if (lastSelectedChatScope) {
            chatScope = lastSelectedChatScope;
        }

        try {
            await chatService
                .createChatAsync(chatTitle, chatScope, await AuthHelper.getSKaaSAccessToken(instance, inProgress))
                .then((result: ICreateChatSessionResponse) => {
                    const newChat: ChatState = {
                        id: result.chatSession.id,
                        title: result.chatSession.title,
                        memoryBalance: result.chatSession.memoryBalance,
                        messages: [result.initialBotMessage],
                        userFeedback: undefined,
                        enabledHostedPlugins: result.chatSession.enabledPlugins,
                        users: [loggedInUser],
                        botProfilePicture: getScopeIcon(result.chatSession.chatScope),
                        input: '',
                        botResponseStatus: undefined,
                        userDataLoaded: false,
                        disabled: false,
                        hidden: false,
                        lastUpdatedTimestamp: result.chatSession.createdOn
                            ? result.chatSession.createdOn
                            : Date.parse(new Date().toISOString()),
                        chatScope: result.chatSession.chatScope,
                        isSaved: false,
                        createdOn: result.chatSession.createdOn,
                    };

                    dispatch(addConversation(newChat));
                    if (!skipNotification) {
                        dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.CREATE_CHAT }]));
                    }
                    return newChat.id;
                });
        } catch (e: any) {
            const errorMessage = `Unable to create new chat. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    const getResponse = async ({
        messageType,
        value,
        chatId,
        kernelArguments,
        processPlan,
        scope,
    }: GetResponseOptions) => {
        const chatInput: IChatMessage = {
            chatId: chatId,
            timestamp: new Date().getTime(),
            userId: activeUserInfo?.id as string,
            userName: activeUserInfo?.username as string,
            content: value,
            type: messageType,
            authorRole: AuthorRoles.User,
        };

        dispatch(addMessageToConversationFromUser({ message: chatInput, chatId: chatId }));

        const ask = {
            input: value,
            variables: [
                {
                    key: 'chatId',
                    value: chatId,
                },
                {
                    key: 'messageType',
                    value: messageType.toString(),
                },
                {
                    key: 'botName',
                    value: Constants.bot.profile.fullName,
                },
            ],
        };

        if (kernelArguments) {
            ask.variables.push(...kernelArguments);
        }

        try {
            await saveChat(chatId, scope);

            const askResult = await chatService
                .getBotResponseAsync(
                    ask,
                    await AuthHelper.getSKaaSAccessToken(instance, inProgress),
                    getEnabledPlugins(),
                    processPlan,
                )
                .catch((e: any) => {
                    throw e;
                });

            // Update token usage of current session
            const responseTokenUsage = askResult.variables.find((v) => v.key === 'tokenUsage')?.value;
            if (responseTokenUsage) dispatch(updateTokenUsage(JSON.parse(responseTokenUsage) as TokenUsage));

            return askResult;
        } catch (e: any) {
            dispatch(updateBotResponseStatus({ chatId, status: undefined }));

            let errorMessage = '';
            const errorDetails = getErrorDetails(e);

            if (
                errorDetails.includes("This model's maximum context length is") ||
                errorDetails.includes('Throttling: Too many requests')
            ) {
                errorMessage = `Token limit: It seems you've reached the token limit.
                    Unfortunately, you can't proceed further until some tokens are freed up.
                    If you need assistance, feel free to reach out to our support team. Thank you for your understanding!`;
            } else if (errorDetails.includes('Error: 429')) {
                errorMessage = `Message limit: Looks like we're having trouble generating a response from the bot right now.
                    It seems we've been a bit too chatty and hit our limit on how often we can talk to the system.
                    Give us 10 seconds to catch our breath, and then feel free to try again.
                    Thanks for your patience and understanding!`;
            } else {
                const action = processPlan ? 'execute plan' : 'generate bot response';
                errorMessage = `Unable to ${action}. Details: ${errorDetails}`;
            }

            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return null;
        }
    };

    const loadChats = async (
        conversations?: Conversations,
        showHidden?: boolean | undefined,
        pageNumber?: number,
        pageSize?: number,
        scrollLoad = false,
    ) => {
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            const chatSessions = await chatService.getAllChatsAsync(
                accessToken,
                showHidden ?? false,
                pageNumber,
                pageSize,
            );

            if (chatSessions !== null && chatSessions.length > 0) {
                const loadedConversations: Conversations = {};

                for (const chatSession of chatSessions) {
                    if (chatSession.id === selectedId) {
                        continue;
                    }

                    const chatMessages: IChatMessage[] = [];
                    const chatUsers: IChatUser[] = [];

                    loadedConversations[chatSession.id] = {
                        id: chatSession.id,
                        title: chatSession.title,
                        memoryBalance: chatSession.memoryBalance,
                        users: chatUsers,
                        messages: chatMessages,
                        userFeedback: undefined,
                        enabledHostedPlugins: chatSession.enabledPlugins,
                        botProfilePicture: getScopeIcon(chatSession.chatScope),
                        input: '',
                        botResponseStatus: undefined,
                        userDataLoaded: false,
                        disabled: false,
                        hidden: chatSession.isHidden,
                        lastUpdatedTimestamp: chatSession.lastModifiedOn,
                        chatScope: chatSession.chatScope,
                        authoritiveDocuments: chatSession.authoritiveDocuments ?? [],
                        selectedSites: chatSession.selectedSites ?? ([] as ISiteInfo[]),
                        threadId: chatSession.threadId,
                        isSaved: true,
                        lockDate: chatSession.lockDate,
                        lockReason: chatSession.lockReason,
                    };
                }

                const chatId = getParamValue('chatid');
                const allConversations = { ...conversations, ...loadedConversations };
                const latestChatId =
                    chatId && allConversations[chatId]
                        ? chatId
                        : conversations?.[selectedId] && !conversations[selectedId].hidden
                          ? selectedId
                          : chatSessions[0].id;

                dispatch(setConversations(allConversations));

                if (!scrollLoad && selectedId === latestChatId) {
                    dispatch(setSelectedConversation(''));
                    setTimeout(() => {
                        dispatch(setSelectedConversation(latestChatId));
                    }, 50);
                } else {
                    dispatch(setSelectedConversation(latestChatId));
                }

                // If there are no non-hidden chats, create a new chat
                if (
                    Object.keys(loadedConversations).length &&
                    Object.values(loadedConversations).filter((c) => !c.hidden).length === 0
                ) {
                    await createChat(true);
                } else {
                    dispatch(setSelectedConversation(latestChatId));
                }
            } else if (!pageNumber) {
                // No chats exist, create first chat window
                await createChat(true);
            }

            return {
                success: true,
                count: chatSessions !== null ? chatSessions.length : 0,
            };
        } catch (e: any) {
            const errorMessage = `Unable to load chats. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return {
                success: false,
                count: 0,
            };
        }
    };

    const loadHiddenChats = async (
        conversations?: Conversations,
        pageNumber?: number,
        pageSize?: number,
        scrollLoad = false,
    ) => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        try {
            const chatSessions = await chatService.getHiddenChatsAsync(accessToken, pageNumber, pageSize);

            if (chatSessions !== null) {
                const loadedConversations: Conversations = {};
                for (const chatSession of chatSessions) {
                    const chatMessages: IChatMessage[] = [];
                    const chatUsers: IChatUser[] = [];

                    loadedConversations[chatSession.id] = {
                        id: chatSession.id,
                        title: chatSession.title,
                        memoryBalance: chatSession.memoryBalance,
                        enabledHostedPlugins: chatSession.enabledPlugins,
                        users: chatUsers,
                        messages: chatMessages,
                        userFeedback: undefined,
                        botProfilePicture: getScopeIcon(chatSession.chatScope),
                        input: '',
                        botResponseStatus: undefined,
                        userDataLoaded: false,
                        disabled: false,
                        hidden: chatSession.isHidden,
                        lastUpdatedTimestamp: chatSession.lastModifiedOn,
                        chatScope: chatSession.chatScope,
                        isSaved: true,
                        lockDate: chatSession.lockDate,
                        lockReason: chatSession.lockReason,
                    };
                }

                const latestChatId = conversations?.[selectedId]?.hidden ? selectedId : chatSessions[0].id;

                const allConversations = { ...(conversations ?? {}), ...loadedConversations };

                dispatch(setConversations(allConversations));
                if (!scrollLoad && selectedId === latestChatId) {
                    dispatch(setSelectedConversation(''));
                    setTimeout(() => {
                        dispatch(setSelectedConversation(latestChatId));
                    }, 50);
                } else {
                    dispatch(setSelectedConversation(latestChatId));
                }
            } else {
                dispatch(setConversations({}));
                dispatch(setSelectedConversation(''));
            }

            return {
                success: true,
                count: chatSessions !== null ? chatSessions.length : 0,
            };
        } catch (e: any) {
            const errorMessage = `Unable to load chats. Details: ${getErrorDetails(e)}`;
            console.error(errorMessage);

            dispatch(setConversations({}));
            dispatch(setSelectedConversation(''));

            return {
                success: false,
                count: 0,
            };
        }
    };

    const getChatMessages = async (chatId: string): Promise<IChatMessage[]> => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        try {
            // get messages and update the redux store
            const messages = await chatService.getChatMessagesAsync(chatId, 0, 100, accessToken);
            dispatch(addMessagesToConversation({ chatId, messages }));

            if (messages?.filter((m) => m.type === 2).length > 0) {
                const documents = await getChatMemorySources(chatId);
                dispatch(setChatMemorySources({ chatId, chatMemorySources: documents }));
            }

            try {
                const userFeedback = await chatService.getUserFeedbackAsync(chatId, accessToken);
                dispatch(setConversationUserFeedback({ chatId, userFeedback: userFeedback }));
            } catch (e: any) {
                const errorMessage = `Unable to load user feedback. Details: ${getErrorDetails(e)}`;
                dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
            }

            return messages;
        } catch (e: any) {
            const errorMessage = `Unable to get chat messages. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return [];
    };

    const downloadBot = async (chatId: string) => {
        try {
            return await botService.downloadAsync(chatId, await AuthHelper.getSKaaSAccessToken(instance, inProgress));
        } catch (e: any) {
            const errorMessage = `Unable to download the bot. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return undefined;
    };

    const uploadBot = async (bot: ChatArchive) => {
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            await botService.uploadAsync(bot, accessToken).then(async (chatSession: IChatSession) => {
                const chatMessages = await chatService.getChatMessagesAsync(chatSession.id, 0, 100, accessToken);

                const newChat: ChatState = {
                    id: chatSession.id,
                    title: chatSession.title,
                    memoryBalance: chatSession.memoryBalance,
                    users: [loggedInUser],
                    messages: chatMessages,
                    userFeedback: undefined,
                    enabledHostedPlugins: chatSession.enabledPlugins,
                    botProfilePicture: getScopeIcon(''),
                    input: '',
                    botResponseStatus: undefined,
                    userDataLoaded: false,
                    disabled: false,
                    hidden: false,
                };

                dispatch(addConversation(newChat));
            });
        } catch (e: any) {
            const errorMessage = `Unable to upload the bot. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    const getChatMemorySources = async (chatId: string) => {
        try {
            return await chatService.getChatMemorySourcesAsync(
                chatId,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
        } catch (e: any) {
            const errorMessage = `Unable to get chat files. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return [];
    };

    const getSemanticMemories = async (chatId: string, memoryName: string) => {
        try {
            return await chatService.getSemanticMemoriesAsync(
                chatId,
                memoryName,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
        } catch (e: any) {
            const errorMessage = `Unable to get semantic memories. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return [];
    };

    const importDocument = async (chatId: string, files: File[], uploadToGlobal: boolean) => {
        try {
            await documentImportService.importDocumentAsync(
                chatId,
                files,
                features[FeatureKeys.AzureContentSafety].enabled,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
                uploadToGlobal,
            );
        } catch (e: any) {
            let errorDetails = getErrorDetails(e);

            // Disable Content Safety if request was unauthorized
            const contentSafetyDisabledRegEx = /Access denied: \[Content Safety] Failed to analyze image./g;
            if (contentSafetyDisabledRegEx.test(errorDetails)) {
                if (features[FeatureKeys.AzureContentSafety].enabled) {
                    errorDetails =
                        'Unable to analyze image. Content Safety is currently disabled or unauthorized service-side. Please contact your admin to enable.';
                }

                dispatch(
                    toggleFeatureState({ feature: FeatureKeys.AzureContentSafety, deactivate: true, enable: false }),
                );
            }

            const errorMessage = `Failed to upload document(s). Details: ${errorDetails}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    /*
     * Once enabled, each plugin will have a custom dedicated header in every Semantic Kernel request
     * containing respective auth information (i.e., token, encoded client info, etc.)
     * that the server can use to authenticate to the downstream APIs
     */
    const getEnabledPlugins = () => {
        return Object.values<Plugin>(plugins).filter((plugin) => plugin.enabled);
    };

    const joinChat = async (chatId: string) => {
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            await chatService.joinChatAsync(chatId, accessToken).then(async (result: IChatSession) => {
                // Get chat messages
                const chatMessages = await chatService.getChatMessagesAsync(result.id, 0, 100, accessToken);

                // Get chat users
                const chatUsers = await chatService.getAllChatParticipantsAsync(result.id, accessToken);

                const newChat: ChatState = {
                    id: result.id,
                    title: result.title,
                    memoryBalance: result.memoryBalance,
                    messages: chatMessages,
                    userFeedback: undefined,
                    enabledHostedPlugins: result.enabledPlugins,
                    users: chatUsers,
                    botProfilePicture: getScopeIcon(result.chatScope),
                    input: '',
                    botResponseStatus: undefined,
                    userDataLoaded: false,
                    disabled: false,
                    hidden: false,
                    chatScope: result.chatScope,
                };

                dispatch(addConversation(newChat));
            });
        } catch (e: any) {
            const errorMessage = `Error joining chat ${chatId}. Details: ${getErrorDetails(e)}`;
            return { success: false, message: errorMessage };
        }

        return { success: true, message: '' };
    };

    const editChat = async (
        chatId: string,
        title: string,
        memoryBalance: number,
        scope?: string,
        selectedSites?: ISiteInfo[],
        isHidden?: boolean,
        authoritiveDocuments?: string[],
        usedBuckets?: string[],
    ) => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        selectedSites = selectedSites ?? [];
        isHidden = isHidden ?? false;
        try {
            //const time = new Date().toISOString();
            await chatService.editChatAsync(
                chatId,
                title,
                memoryBalance,
                accessToken,
                scope,
                isHidden,
                selectedSites,
                authoritiveDocuments ?? [],
                usedBuckets ?? []
            );
        } catch (e: any) {
            const errorMessage = `Error editing chat ${chatId}. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    const saveChat = async (chatId: string, newScope?: string) => {
        const conversation = conversations[chatId];
        if (!conversation.isSaved) {
            // If a new scope is provided, use it; otherwise use the conversation's current scope
            const conversationToSave = newScope ? { ...conversation, chatScope: newScope } : conversation;
            await chatService.saveChatAsync(
                conversationToSave,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
            dispatch(editConversationIsSaved({ id: chatId, isSaved: true }));
        }
    };

    const editMessage = async (
        chatId: string,
        messageId: string,
        userFeedbackType: UserFeedbackType,
        userFeedbackComment?: string,
    ): Promise<UserFeedback> => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        return await chatService.editUserFeedbackAsync(
            chatId,
            messageId,
            userFeedbackType,
            userFeedbackComment,
            accessToken,
        );
    };

    const getServiceInfo = async () => {
        try {
            return await chatService.getServiceInfoAsync(await AuthHelper.getSKaaSAccessToken(instance, inProgress));
        } catch (e: any) {
            const errorMessage = `Error getting service options. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return undefined;
        }
    };

    const deleteChat = async (chatId: string) => {
        await chatService
            .deleteChatAsync(chatId, await AuthHelper.getSKaaSAccessToken(instance, inProgress))
            .then(() => {
                dispatch(deleteConversation(chatId));
                dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.DELETE_CHAT }]));

                if (Object.values(conversations).filter((c) => !c.hidden && c.id !== chatId).length === 0) {
                    // If there are no non-hidden chats, create a new chat
                    void createChat(true);
                }
            })
            .catch((e: any) => {
                const errorDetails = (e as Error).message.includes('Failed to delete resources for chat id')
                    ? "Some or all resources associated with this chat couldn't be deleted. Please try again."
                    : `Details: ${(e as Error).message}`;
                dispatch(
                    addAlert({
                        message: `Unable to delete chat {${conversations[chatId].title}}. ${errorDetails}`,
                        type: AlertType.Error,
                        onRetry: () => void deleteChat(chatId),
                    }),
                );
            });
    };

    const processPlan = async (chatId: string, planState: PlanState, serializedPlan: string, planGoal?: string) => {
        const kernelArguments: ContextVariable[] = [
            {
                key: 'proposedPlan',
                value: serializedPlan,
            },
        ];

        let message = 'Run plan' + (planGoal ? ` with goal of: ${planGoal}` : '');
        switch (planState) {
            case PlanState.Rejected:
                message = 'No, cancel';
                break;
            case PlanState.Approved:
                message = 'Yes, proceed';
                break;
        }

        // Send plan back for processing or execution
        await getResponse({
            value: message,
            kernelArguments,
            messageType: ChatMessageType.Message,
            chatId: chatId,
            processPlan: true,
        });
    };

    const getChatParticipants = async (chatId: string) => {
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            const chatParticipants: IChatUser[] = await chatService.getAllChatParticipantsAsync(chatId, accessToken);

            dispatch(setConversationParticipants({ chatId, users: chatParticipants }));

            return true;
        } catch (e: any) {
            const errorMessage = `Unable to load chat participants. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return false;
        }
    };

    const getUserPermissions = async () => {
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            const result: IUserPermissions = await chatService.getUserPermissionsAsync(accessToken);

            if (result.isChatDeletionAllowed) {
                dispatch(toggleFeatureState({ feature: FeatureKeys.DeleteChat, deactivate: false, enable: true }));
            }
        } catch (error) {
            /* Do nothing, leave feature disabled */
        }
    };
    return {
        getChatUserById,
        createChat,
        loadChats,
        loadHiddenChats,
        getResponse,
        downloadBot,
        uploadBot,
        getChatMemorySources,
        getSemanticMemories,
        importDocument,
        joinChat,
        editChat,
        saveChat,
        editMessage,
        getServiceInfo,
        deleteChat,
        processPlan,
        getChatParticipants,
        getChatMessages,
        getUserPermissions,
    };
};
